import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import TiptapCompactEditor from '../fields/rte/TiptapCompactEditor';
import TiptapStaticRenderer from '../fields/rte/TiptapStaticRenderer';
import Icon from '../../misc/Icon';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTH = styled.div`
  box-sizing: border-box;
  background-color: ${(props) => props.theme.secondaryColor};
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorPrimary};
  border-top: 1px solid ${(props) => props.theme.borderColor};
  border-left: 1px solid ${(props) => props.theme.borderColor};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  border-right: 1px solid ${(props) => props.theme.borderColor};
  padding: 4px;
  max-width: 240px;
  min-width: 240px;
  vertical-align: top;
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
`;

const StyledEditorWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 40px;
  max-width: 200px;
  overflow: hidden;

  .tiptap.ProseMirror {
    min-height: 24px;
    padding: 2px 4px;
    font-size: 12px;
    font-weight: 600;
  }
`;

const StyledIconWrapper = styled.span`
  padding-left: 4px;
  flex-shrink: 0;
  svg {
    font-size: 14px;
  }
`;

const StyledActionsWrapper = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

class TableEditorRteHeaderCell extends Component {

  static contextType = ThemeContext

  static propTypes = {
    id: PropTypes.string,
    column: PropTypes.object,
    columnIndex: PropTypes.number,
    isFirst: PropTypes.bool,
    isLast: PropTypes.bool,
    onBlur: PropTypes.func,
    onMoveLeft: PropTypes.func,
    onMoveRight: PropTypes.func,
    onDelete: PropTypes.func,
  }

  constructor(props) {
    super(props);
    this.state = { editing: false, localContent: props.column?.title || '' };
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.editing !== this.state.editing) return true;
    if (nextState.localContent !== this.state.localContent) return true;
    return nextProps.column?._id !== this.props.column?._id
      || nextProps.columnIndex !== this.props.columnIndex
      || nextProps.isFirst !== this.props.isFirst
      || nextProps.isLast !== this.props.isLast;
  }

  componentDidUpdate(prevProps) {
    if (!this.state.editing && prevProps.column?._id !== this.props.column?._id) {
      this.setState({ localContent: this.props.column?.title || '' });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.blurTimer);
  }

  handleBlur = (json) => {
    this.latestJson = json;
    clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => {
      const { onBlur, column } = this.props;
      if (onBlur) onBlur(column._id, { title: this.latestJson });
      this.setState({ editing: false, localContent: this.latestJson });
    }, 150);
  }

  handleFocus = () => {
    clearTimeout(this.blurTimer);
  }

  handleStaticClick = () => {
    this.setState({ editing: true });
  }

  render() {
    const {
      id,
      column,
      isFirst,
      isLast,
      onMoveLeft,
      onMoveRight,
      onDelete,
    } = this.props;

    const { borderColor, primaryColor, dangerColor } = this.context;

    const columnMoveLeft = isFirst ? null : (
      <StyledIconWrapper>
        <Icon
          id={`${id}-${column._id}-rte-editor-column-move-left`}
          color={borderColor}
          hoverColor={primaryColor}
          size="lg"
          icon={ThemeIcons.tableSettings.columnMoveLeft}
          onClick={() => onMoveLeft(column._id)}
        />
      </StyledIconWrapper>
    );

    const columnMoveRight = isLast ? null : (
      <StyledIconWrapper>
        <Icon
          id={`${id}-${column._id}-rte-editor-column-move-right`}
          color={borderColor}
          size="lg"
          hoverColor={primaryColor}
          icon={ThemeIcons.tableSettings.columnMoveRight}
          onClick={() => onMoveRight(column._id)}
        />
      </StyledIconWrapper>
    );

    const columnDelete = (
      <StyledIconWrapper>
        <Icon
          id={`${id}-${column._id}-rte-editor-column-delete`}
          color={dangerColor}
          hoverColor={dangerColor}
          icon={ThemeIcons.settings.delete}
          onClick={() => onDelete(column._id)}
        />
      </StyledIconWrapper>
    );

    return (
      <StyledTH theme={this.context} data-testid={`${id}-${column._id}-${this.props.columnIndex}-rte-editor-column-th`}>
        <StyledTitleWrapper>
          <StyledEditorWrapper>
            {this.state.editing ? (
              <TiptapCompactEditor
                autoFocus="end"
                content={this.state.localContent}
                disabled={false}
                onUpdate={() => {}}
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                borderColor="transparent"
                borderRadius={4}
                borderWidth={1}
                padding={2}
                fontSize={12}
              />
            ) : (
              <TiptapStaticRenderer
                content={this.state.localContent}
                onClick={this.handleStaticClick}
              />
            )}
          </StyledEditorWrapper>
          <StyledActionsWrapper>
            {columnMoveLeft}
            {columnMoveRight}
            {columnDelete}
          </StyledActionsWrapper>
        </StyledTitleWrapper>
      </StyledTH>
    );
  }
}

class TableEditorRteHeader extends Component {

  static contextType = ThemeContext

  static propTypes = {
    id: PropTypes.string,
    rteColumns: PropTypes.array,
    onBlur: PropTypes.func,
    onMoveLeft: PropTypes.func,
    onMoveRight: PropTypes.func,
    onDelete: PropTypes.func,
  }

  shouldComponentUpdate(nextProps) {
    const prev = this.props.rteColumns || [];
    const next = nextProps.rteColumns || [];
    if (prev.length !== next.length) return true;
    for (let i = 0; i < prev.length; i++) {
      if (prev[i]._id !== next[i]._id) return true;
    }
    return false;
  }

  render() {

    const {
      id,
      rteColumns,
      onBlur,
      onMoveLeft,
      onMoveRight,
      onDelete,
    } = this.props;

    const columns = rteColumns || [];

    const columnNodes = columns.map((column, columnIndex) => (
      <TableEditorRteHeaderCell
        key={column._id}
        id={id}
        column={column}
        columnIndex={columnIndex}
        isFirst={columnIndex === 0}
        isLast={columnIndex === columns.length - 1}
        onBlur={onBlur}
        onMoveLeft={onMoveLeft}
        onMoveRight={onMoveRight}
        onDelete={onDelete}
      />
    ));

    return (
      <StyledRow>
        {columnNodes}
      </StyledRow>
    );

  }

}

export default TableEditorRteHeader;
